import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Key, Check, X, Activity, Zap, AlertCircle, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to safely parse fetch responses
// This ensures each response body is consumed exactly ONCE
// Handles the case where an external fetch interceptor may have already consumed the body
async function parseResponse(response) {
  console.log('[parseResponse] Reading response body for status:', response.status);
  
  let rawText = '';
  let data = {};
  
  try {
    // Try to read the body
    rawText = await response.text();
    console.log('[parseResponse] Body read complete, length:', rawText.length);
  } catch (bodyError) {
    // If body is already read (by a fetch interceptor), handle gracefully
    console.error('[parseResponse] Body already consumed:', bodyError.message);
    
    // For 401 errors, we know it's an auth issue
    if (response.status === 401) {
      throw new Error('Authentication token not found. Please log in again.');
    }
    
    // For other errors, use status text
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }
  
  // Parse JSON
  try {
    data = rawText ? JSON.parse(rawText) : {};
  } catch (parseError) {
    console.error('[parseResponse] JSON parse failed:', parseError);
    data = { message: rawText };
  }

  if (!response.ok) {
    const errorMessage = data.detail || data.message || `Request failed: ${response.status}`;
    console.error('[parseResponse] Response not OK:', errorMessage);
    throw new Error(errorMessage);
  }

  console.log('[parseResponse] Success, returning parsed data');
  return data;
}

export function StripeConfigurationPanel({ token: propToken, onClose }) {
  // Get fresh token from localStorage using correct key
  const [token] = useState(() => propToken || localStorage.getItem('msb_token'));
  
  const [config, setConfig] = useState({
    secretKey: '',
    publishableKey: '',
    webhookSecret: ''
  });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  // Check token validity
  useEffect(() => {
    if (!token) {
      setError('Authentication token not found. Please log in again.');
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Stripe] Token available:', token.substring(0, 20) + '...');
      }
    }
  }, [token]);

  const loadHealthStatus = useCallback(async () => {
    console.log('[Stripe TRACE] loadHealthStatus START');
    try {
      console.log('[Stripe TRACE] Health - FETCH START');
      const response = await fetch(`${BACKEND_URL}/api/stripe/health`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('[Stripe TRACE] Health - Response received, status:', response.status);
      
      if (response.ok) {
        console.log('[Stripe TRACE] Health - About to read body with .json()');
        const data = await response.json();
        console.log('[Stripe TRACE] Health - Body read complete with .json()');
        setHealth(data);
        console.log('[Stripe TRACE] Health - Data set');
      } else {
        console.log('[Stripe TRACE] Health - Response not OK, skipping body read');
      }
    } catch (err) {
      console.error('[Stripe TRACE] Health - ERROR:', err.message);
      console.error('[Stripe TRACE] Health - Error stack:', err.stack);
    }
    console.log('[Stripe TRACE] loadHealthStatus COMPLETE');
  }, [token]);

  // Load health status on mount
  useEffect(() => {
    if (token) {
      loadHealthStatus();
    }
  }, [token, loadHealthStatus]);

  const handleSave = async () => {
    console.log('[Stripe TRACE] handleSave START - Atomic Save');
    
    // Check token first
    if (!token) {
      setError('Session expired. Please log in again.');
      setSaving(false);
      setTimeout(() => {
        onClose();
        window.location.href = '/';
      }, 2000);
      return;
    }
    
    setError(null);
    setSaving(true);
    setValidationResult(null);

    try {
      // Atomic save - all three credentials at once
      console.log('[Stripe TRACE] Saving all credentials atomically');
      const response = await fetch(`${BACKEND_URL}/api/stripe/configure`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          secret_key: config.secretKey,
          publishable_key: config.publishableKey,
          webhook_secret: config.webhookSecret
        })
      });

      const data = await parseResponse(response);
      console.log('[Stripe TRACE] Atomic save complete');
      
      // Run validation
      console.log('[Stripe TRACE] Running validation');
      const validationResponse = await fetch(`${BACKEND_URL}/api/stripe/validate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const validation = await parseResponse(validationResponse);
      setValidationResult(validation);

      setSaved(true);
      setConfig({ secretKey: '', publishableKey: '', webhookSecret: '' });
      
      // Reload health status
      console.log('[Stripe TRACE] Reloading health status');
      await loadHealthStatus();
      console.log('[Stripe TRACE] handleSave COMPLETE');

    } catch (err) {
      console.error('[Stripe TRACE] handleSave ERROR:', err.message);
      
      // Check for authentication errors
      if (err.message.includes('401') || err.message.includes('Authentication')) {
        setError('Session expired. Please log in again.');
        setTimeout(() => {
          onClose();
          window.location.href = '/';
        }, 2000);
      } else {
        setError(err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setError(null);

    try {
      console.log('[Stripe TRACE] Testing connection - Build v10-STRIPE-CHECKOUT');
      const response = await fetch(`${BACKEND_URL}/api/stripe/test-connection`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await parseResponse(response);
      console.log('[Stripe TRACE] Connection test complete');
      setValidationResult(result);
      await loadHealthStatus();
    } catch (err) {
      console.error('[Stripe TRACE] Test connection error:', err);
      setError(err.message);
    } finally {
      setTesting(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'healthy') return <Check size={16} className="text-green-400" />;
    if (status === 'warning') return <AlertCircle size={16} className="text-yellow-400" />;
    return <X size={16} className="text-red-400" />;
  };

  const getStatusColor = (status) => {
    if (status === 'healthy') return 'text-green-400';
    if (status === 'warning') return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px',
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div 
        className="stripe-config-panel glass-panel" 
        style={{ 
          maxWidth: '800px', 
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="panel-header" style={{ borderBottom: '1px solid rgba(212, 166, 42, 0.2)', paddingBottom: '1rem', marginBottom: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Shield size={24} style={{ color: '#d4a62a' }} />
            <div>
              <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#f7f3ea' }}>Stripe Configuration</h2>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: 'rgba(247, 243, 234, 0.6)' }}>
                Securely configure your Stripe API keys • Build: 2026-06-16-v10-STRIPE-CHECKOUT
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: '1px solid rgba(212, 166, 42, 0.3)',
              color: '#f7f3ea',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            data-testid="stripe-config-close-button"
          >
            <X size={20} />
          </button>
        </div>

      {/* Health Status */}
      {health && (
        <div style={{ marginBottom: '2rem', padding: '1.5rem', background: 'rgba(7, 7, 7, 0.5)', borderRadius: '8px', border: '1px solid rgba(212, 166, 42, 0.2)' }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#d4a62a', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Activity size={18} />
            Stripe Health Status
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {getStatusIcon(health.checks?.api_key?.status)}
              <span className={getStatusColor(health.checks?.api_key?.status)}>
                API Key: {health.checks?.api_key?.mode || 'Not Configured'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {getStatusIcon(health.checks?.webhook_secret?.status)}
              <span className={getStatusColor(health.checks?.webhook_secret?.status)}>
                Webhook: {health.checks?.webhook_secret?.configured ? 'Connected' : 'Not Configured'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {health.live_mode ? <Check size={16} className="text-green-400" /> : <X size={16} className="text-yellow-400" />}
              <span className={health.live_mode ? 'text-green-400' : 'text-yellow-400'}>
                Mode: {health.live_mode ? 'LIVE' : 'TEST'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#f7f3ea', fontSize: '0.875rem', fontWeight: 500 }}>
            <Lock size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
            Stripe Live Secret Key
          </label>
          <input
            type="password"
            placeholder="sk_live_..."
            value={config.secretKey}
            onChange={(e) => setConfig({ ...config, secretKey: e.target.value })}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'rgba(7, 7, 7, 0.5)',
              border: '1px solid rgba(212, 166, 42, 0.3)',
              borderRadius: '6px',
              color: '#f7f3ea',
              fontSize: '0.875rem',
              fontFamily: 'monospace'
            }}
            data-testid="stripe-secret-key-input"
          />
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', color: 'rgba(247, 243, 234, 0.5)' }}>
            Your Stripe Secret Key will be encrypted before storage and never displayed again
          </p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#f7f3ea', fontSize: '0.875rem', fontWeight: 500 }}>
            <Key size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
            Stripe Publishable Key
          </label>
          <input
            type="text"
            placeholder="pk_live_..."
            value={config.publishableKey}
            onChange={(e) => setConfig({ ...config, publishableKey: e.target.value })}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'rgba(7, 7, 7, 0.5)',
              border: '1px solid rgba(212, 166, 42, 0.3)',
              borderRadius: '6px',
              color: '#f7f3ea',
              fontSize: '0.875rem',
              fontFamily: 'monospace'
            }}
            data-testid="stripe-publishable-key-input"
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', color: '#f7f3ea', fontSize: '0.875rem', fontWeight: 500 }}>
            <Shield size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
            Webhook Signing Secret
          </label>
          <input
            type="password"
            placeholder="whsec_..."
            value={config.webhookSecret}
            onChange={(e) => setConfig({ ...config, webhookSecret: e.target.value })}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'rgba(7, 7, 7, 0.5)',
              border: '1px solid rgba(212, 166, 42, 0.3)',
              borderRadius: '6px',
              color: '#f7f3ea',
              fontSize: '0.875rem',
              fontFamily: 'monospace'
            }}
            data-testid="stripe-webhook-secret-input"
          />
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.75rem', color: 'rgba(247, 243, 234, 0.5)' }}>
            Found in Stripe Dashboard → Webhooks → Select your endpoint → Signing secret
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div style={{ padding: '1rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '6px', marginBottom: '1rem' }}>
          <p style={{ margin: 0, color: '#ef4444', fontSize: '0.875rem' }}>
            <AlertCircle size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
            {error}
          </p>
        </div>
      )}

      {/* Validation Result */}
      {validationResult && validationResult.status && (
        <div style={{ 
          padding: '1.5rem', 
          background: validationResult.status === 'PASS' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
          border: `1px solid ${validationResult.status === 'PASS' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`, 
          borderRadius: '8px', 
          marginBottom: '1rem' 
        }}>
          <h3 style={{ 
            margin: '0 0 1rem 0', 
            fontSize: '1rem', 
            color: validationResult.status === 'PASS' ? '#22c55e' : '#ef4444',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {validationResult.status === 'PASS' ? <Check size={20} /> : <X size={20} />}
            Validation {validationResult.status}
          </h3>
          {validationResult.checks && Object.keys(validationResult.checks).length > 0 && (
            <div style={{ fontSize: '0.875rem', color: '#f7f3ea', marginBottom: '0.5rem' }}>
              {Object.entries(validationResult.checks).map(([key, value]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  {value.passed ? <Check size={14} style={{ color: '#22c55e', flexShrink: 0 }} /> : <X size={14} style={{ color: '#ef4444', flexShrink: 0 }} />}
                  <span style={{ color: value.passed ? '#f7f3ea' : '#fca5a5' }}>{value.message}</span>
                </div>
              ))}
            </div>
          )}
          {validationResult.warnings && validationResult.warnings.length > 0 && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.3)', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600, color: '#fbbf24', fontSize: '0.875rem' }}>⚠️ Warnings:</p>
              <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#fbbf24', fontSize: '0.875rem' }}>
                {validationResult.warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
          {validationResult.blockers && validationResult.blockers.length > 0 && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '4px' }}>
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600, color: '#ef4444', fontSize: '0.875rem' }}>🚫 Blockers:</p>
              <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#fca5a5', fontSize: '0.875rem' }}>
                {validationResult.blockers.map((blocker, idx) => (
                  <li key={idx}>{blocker}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Success Message */}
      {saved && !error && (
        <div style={{ padding: '1rem', background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '6px', marginBottom: '1rem' }}>
          <p style={{ margin: 0, color: '#22c55e', fontSize: '0.875rem' }}>
            <Check size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
            Stripe configuration saved successfully and encrypted
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
        <button
          onClick={handleTestConnection}
          disabled={testing || !health?.checks?.api_key?.configured}
          className="ghost-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          data-testid="stripe-test-connection-button"
        >
          <Zap size={16} />
          {testing ? 'Testing...' : 'Test Connection'}
        </button>
        <button
          onClick={handleSave}
          disabled={saving || (!config.secretKey && !config.publishableKey && !config.webhookSecret)}
          className="primary-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          data-testid="stripe-save-config-button"
        >
          <Lock size={16} />
          {saving ? 'Saving...' : 'Save & Validate'}
        </button>
      </div>
    </div>
    </div>
  );
}
